import React from "react"
import { Link } from "react-router"

import { useI18n } from "@/lib/i18n"
import {
  faLinkedin,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons"
import { faEnvelope, faLocationDot } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { PorematLogo } from "./poremat-logo"

export const Footer: React.FC = () => {
  const { t, locale } = useI18n()

  const quickLinks = [
    { label: t("nav.company"), path: "/company" },
    { label: t("nav.ourApproach"), path: "/our-approach" },
    { label: t("nav.products"), path: "/products" },
    { label: t("nav.resources"), path: "/resources" },
    { label: t("nav.contact"), path: "/contact" },
  ]

  const legalLinks = [
    { label: t("footer.privacyPolicy"), path: "/privacy-policy" },
    { label: t("footer.cookiePolicy"), path: "/cookie-policy" },
  ]

  const socialLinks = [
    {
      icon: faLinkedin,
      href: "https://linkedin.com/company/poremat",
      label: "LinkedIn",
    },
    { icon: faInstagram, href: "https://instagram.com/poremat__", label: "Instagram" },
    { icon: faEnvelope, href: "mailto:porematmofs@gmail.com", label: "Email" },
  ]

  return (
    <footer className="bg-linear-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* About */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <PorematLogo className="h-10 w-auto brightness-0 invert" />
            </Link>
            <p className="text-gray-300 text-sm mb-6 leading-relaxed">
              {locale === "tr"
                ? "MOF tabanlı gözenekli malzemelerle temiz enerji, karbon yakalama ve sürdürülebilir teknoloji çözümleri geliştiriyoruz."
                : "We develop MOF-based porous materials for clean energy, carbon capture, and sustainable technology solutions."}
            </p>
            <div className="flex items-center space-x-2 text-gray-300 text-sm mb-3">
              <FontAwesomeIcon
                icon={faLocationDot}
                className="w-4 h-4 text-[#568F22]"
              />
              <span>75.⁠ ⁠Yıl Osb Mah. Bilim Cad. No: 5 İç Kapı No: 503 Odunpazarı / Eskişehir</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-300 text-sm">
              <FontAwesomeIcon
                icon={faEnvelope}
                className="w-4 h-4 text-[#568F22]"
              />
              <a
                href="mailto:porematmofs@gmail.com"
                className="hover:text-[#568F22] transition-colors"
              >
                porematmofs@gmail.com
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">
              {t("footer.quickLinks")}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-[#568F22] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">
              Legal
            </h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-[#568F22] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social*/}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">
              {t("footer.followUs")}
            </h3>
            <div className="flex space-x-4 mb-8">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#568F22] transition-all duration-300 group"
                  aria-label={social.label}
                >
                  <FontAwesomeIcon
                    icon={social.icon}
                    className="w-5 h-5 text-gray-300 group-hover:text-white"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-gray-700 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} Poremat.{" "}
            {locale === "tr" ? "Tüm hakları saklıdır." : "All rights reserved."}
          </p>
          <p className="text-sm text-gray-400">
            {locale === "tr" ? (
              <>
                <a
                  href="https://humrix.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#568F22] font-medium hover:text-[#0077B6] transition-colors"
                >
                  Humrix
                </a>
                {" tarafından geliştirildi."}
              </>
            ) : (
              <>
                Developed by{" "}
                <a
                  href="https://humrix.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#568F22] font-medium hover:text-[#0077B6] transition-colors"
                >
                  Humrix
                </a>
                .
              </>
            )}
          </p>
        </div>
      </div>
    </footer>
  )
}
